/**
 * Cross-module X XWP-15 (refonte WP-40; design/ux/04_produits_stock_assistant.md §2.1; X 02_DESIGN
 * §3.1, §3.3) — the pure model of « Stock & lots »: the request parameters from the URL state, the
 * views strip (DB-wide counts of `GET /stock/alertes/compteurs`, labels from the server thresholds),
 * the facet options, the active-filter chips, the cells of a lot row and of a product row, and the
 * targeted refresh rule (which SSE keys concern the page). Rendering lives in
 * pages/stock/StockDashboard.jsx; nothing here touches the DOM.
 */
import { STOCK_LOTS as M, ETATS_LOT_UI, NIVEAUX_ALERTE_UI } from "@/config/stock.messages.js";
import { STOCK_PARAMS, STOCK_VIEWS, STOCK_VUES_ORDRE } from "@/config/stock-views.config.js";
import { SORT_PARAMS } from "@/config/ui.config.js";
import { LOCALE_LABELS } from "@/config/locale.config.js";
import { texteMoisPeremption } from "@/lib/etiquettes-lots.js";
import { CARTE_LOT } from "@/config/produits.messages.js";
import { TON_PEREMPTION } from "@/lib/panneau-produit.js";
import { CHAMP_TYPE, producteurDuType } from "@/config/produit-type-fields.config.js";

/** « Non renseigné » value of the laboratoire / marque facets (server FACETTES_STOCK.non_renseigne). */
export const NON_RENSEIGNE = "__non_renseigne__";

/** The filter definitions of the page, in bar order; `facet` = the key of `GET /stock/facettes`. */
export const FILTRES_STOCK = Object.freeze([
  Object.freeze({ key: "type_produit", facet: "type_produit", type: "multi", icon: "pill", lots: true, produits: true }),
  Object.freeze({ key: "laboratoire_id", facet: "laboratoire", type: "multi", icon: "flask-conical", searchable: true, lots: true, produits: true }),
  Object.freeze({ key: "marque", facet: "marque", type: "multi", icon: "tag", searchable: true, lots: true, produits: true }),
  Object.freeze({ key: "magasin_id", facet: "magasin", type: "single", icon: "warehouse", lots: true, produits: false }),
  Object.freeze({ key: "peremption_avant", type: "date", icon: "calendar-clock", lots: true, produits: false }),
]);

/**
 * Refonte WP-101 (CP §15.6 « derive the source fields from product type », D-42) — which producer
 * facet the selected types make relevant: no type selected → both; otherwise the laboratoire when a
 * selected type's base carries one (Médicament, Complément alimentaire, an owner type of those bases),
 * the marque when one carries a marque — a mixed selection keeps both (each speaks for part of it,
 * A-113). A type whose base is unknown leaves both active (no rule invented).
 * @param {string[]|null|undefined} types  the « Type » filter values
 * @param {Record<string, string|{ type_base?: string }>} [bases]  value → its catalogue type / base (owner types)
 * @returns {{ laboratoire_id: boolean, marque: boolean }}
 */
export function facettesProducteur(types, bases = {}) {
  const choisis = (Array.isArray(types) ? types : []).filter((v) => v != null && v !== "");
  if (choisis.length === 0) return { laboratoire_id: true, marque: true };
  const actives = { laboratoire_id: false, marque: false };
  for (const v of choisis) {
    const p = producteurDuType(bases[v] ?? v);
    if (p === CHAMP_TYPE.LABORATOIRE) actives.laboratoire_id = true;
    else if (p === CHAMP_TYPE.MARQUE) actives.marque = true;
    else return { laboratoire_id: true, marque: true };
  }
  return actives;
}

/** The facet keys `facettesProducteur` governs. */
export const FACETTES_PRODUCTEUR = Object.freeze(["laboratoire_id", "marque"]);

/**
 * The filter definitions with the producer facet the types do not use greyed (`disabled` + its
 * reason, FilterDropdown renders it aria-disabled and never opens it).
 * @param {object[]} definitions
 * @param {{ laboratoire_id: boolean, marque: boolean }} actives
 */
export function definitionsAvecProducteur(definitions, actives) {
  const raison = { laboratoire_id: M.filtres.laboratoireSansObjet, marque: M.filtres.marqueSansObjet };
  return definitions.map((d) =>
    FACETTES_PRODUCTEUR.includes(d.key) && actives[d.key] === false ? { ...d, disabled: true, disabledReason: raison[d.key] } : d,
  );
}

/** The filters the request applies: a producer facet the types do not use is never applied. */
export function filtresAppliques(filtres, actives) {
  const out = { ...(filtres ?? {}) };
  for (const k of FACETTES_PRODUCTEUR) if (actives[k] === false) delete out[k];
  return out;
}

/** The producer facet holding values the types no longer use (the page clears it from the URL), or null. */
export function facetteProducteurAEffacer(valeurs, actives) {
  return FACETTES_PRODUCTEUR.find((k) => actives[k] === false && Array.isArray(valeurs?.[k]) && valeurs[k].length > 0) ?? null;
}

/**
 * The applied-filter chips with the option's label (EXE-95, raised and resolved by refonte WP-101): the
 * page's filters are declared without options (they come from the facets answer), so `useFilters`
 * names a chip by its raw value — « Type : COMPLEMENT_ALIMENTAIRE », a laboratoire's id. The labels are
 * the facet options' (French type labels, laboratoire names, « Non renseigné »); a value no option
 * names keeps its chip as it is.
 * @param {{ id: string, defKey: string, label: string }[]} chips  `useFilters().chips`
 * @param {{ key: string, label: string, type: string, options?: { value: unknown, label: string }[] }[]} definitions
 * @param {Record<string, unknown>} valeurs  `useFilters().values`
 */
export function chipsLibelles(chips, definitions, valeurs) {
  const parCle = new Map((definitions ?? []).map((d) => [d.key, d]));
  return (chips ?? []).map((c) => {
    const d = parCle.get(c.defKey);
    if (!d?.options?.length) return c;
    const valeur = d.type === "multi" ? c.id.slice(c.defKey.length + 1) : valeurs?.[c.defKey];
    const o = d.options.find((x) => String(x.value) === String(valeur));
    return o ? { ...c, label: `${d.label} : ${o.label}` } : c;
  });
}

/** Sortable columns of each row kind (server STOCK_TRI / STOCK_TRI_PRODUITS); FEFO by default. */
export const TRI_LOTS = Object.freeze([
  "produit",
  "numero_lot",
  "magasin",
  "date_peremption",
  "quantite",
  "ppa",
  "laboratoire",
  "type",
  "entree",
  "vendable",
  // X XWP-97 (D-49) — « Prix HT » (P / M; the server refuses it to a CAISSIER) and « Stock initial »
  "prix_ht",
  "stock_initial",
]);
export const TRI_PRODUITS = Object.freeze(["produit", "laboratoire", "type", "vendable", "seuil_alerte", "qte_min"]);
export const TRI_DEFAUT = Object.freeze({
  lots: Object.freeze({ by: "date_peremption", order: "asc" }),
  produits: Object.freeze({ by: "vendable", order: "asc" }),
});

/** A view value as the page may show it (an unknown one reads as the default view). */
export function vueValide(vue, vuesPermises = null) {
  const v = STOCK_VUES_ORDRE.some((x) => x.value === vue) ? vue : STOCK_VIEWS.TOUS;
  if (vuesPermises && !vuesPermises.includes(v)) return STOCK_VIEWS.TOUS;
  return v;
}

/** What a row of the view is: `lots` or `produits`. */
export function lignesDeLaVue(vue) {
  return STOCK_VUES_ORDRE.find((x) => x.value === vue)?.lignes ?? "lots";
}

/** The label of a view (the péremption ones from the server thresholds: « Pér. ≤ 30 j »). */
export function libelleVue(def, seuils, { court = false } = {}) {
  if (def.seuil) {
    const j = seuils?.[def.seuil];
    return court ? `≤ ${j ?? "…"} j` : `Pér. ≤ ${j ?? "…"} j`;
  }
  return court ? def.court : def.label;
}

/**
 * The views strip: one chip per view the role may open, each with its DB-wide count (never the
 * filtered request total, AUD-STOCK-20), or none while the counts load (never « 0 »).
 * @param {{ vues?: Record<string, number>, seuils?: Record<string, number> }|null} compteurs
 */
export function vuesDuBandeau(compteurs) {
  const vues = compteurs?.vues ?? null;
  return STOCK_VUES_ORDRE.filter((d) => d.value !== STOCK_VIEWS.BLOQUES || (vues && vues.bloques !== undefined)).map((d) => ({
    value: d.value,
    label: libelleVue(d, compteurs?.seuils),
    court: libelleVue(d, compteurs?.seuils, { court: true }),
    count: vues ? (vues[d.value] ?? 0) : undefined,
    lignes: d.lignes,
  }));
}

/** Comma-joined multi values (the server splits on «,»). */
const joindre = (v) => (Array.isArray(v) ? v.filter((x) => x != null && x !== "").join(",") : v);

/**
 * The GET /stock/lots parameters of the page (and, minus paging / sort, of GET /stock/facettes).
 * Filters that do not apply to the product views (magasin, péremption, épuisés) are left out there.
 *
 * @param {{ vue: string, filtres: Record<string, unknown>, recherche?: string, produitId?: string|null,
 *   inclureEpuises?: boolean, tri?: {by:string,order:string}|null, page: number, limite: number }} p
 */
export function parametresStock({ vue, filtres, recherche, produitId, inclureEpuises, tri, page, limite }) {
  const lignes = lignesDeLaVue(vue);
  const out = { page, limit: limite };
  if (vue && vue !== STOCK_VIEWS.TOUS) out.vue = vue;
  for (const def of FILTRES_STOCK) {
    if (lignes === "produits" && !def.produits) continue;
    const j = joindre(filtres?.[def.key]);
    if (j != null && j !== "") out[def.key] = j;
  }
  if (produitId) out.produit_id = produitId;
  if (lignes === "lots" && inclureEpuises && (vue ?? STOCK_VIEWS.TOUS) === STOCK_VIEWS.TOUS) out.inclure_epuises = "true";
  const q = String(recherche ?? "").trim();
  if (q) out.search = q;
  const t = tri ?? TRI_DEFAUT[lignes];
  out[SORT_PARAMS.BY] = t.by;
  out[SORT_PARAMS.ORDER] = t.order;
  return out;
}

/** True when anything narrows the view (search, filter, product). */
export function vueFiltree({ filtres, recherche, produitId }) {
  if (String(recherche ?? "").trim() || produitId) return true;
  return Object.values(filtres ?? {}).some((v) => (Array.isArray(v) ? v.length > 0 : v != null && v !== ""));
}

/** The sort a URL may carry for this row kind (else the kind's default). */
export function triDeLaVue(vue, tri) {
  const lignes = lignesDeLaVue(vue);
  const permis = lignes === "produits" ? TRI_PRODUITS : TRI_LOTS;
  return tri && permis.includes(tri.by) ? tri : TRI_DEFAUT[lignes];
}

/** « 1–50 sur 83 » bounds and the page count. */
export function bornesPage(page, limite, total) {
  const pages = Math.max(1, Math.ceil((Number(total) || 0) / limite));
  const debut = total ? (page - 1) * limite + 1 : 0;
  const fin = Math.min(total ?? 0, page * limite);
  return { debut, fin, pages };
}

// ─── Cells of a lot row (ux/04 §2.1.2) ──────────────────────────────────────────────────────────

const dci = (p) => (typeof p?.dci === "object" ? p?.dci?.nom : p?.dci) ?? null;
const forme = (p) => (typeof p?.forme === "object" ? p?.forme?.libelle : p?.forme) ?? null;

/**
 * Line 2 of « Produit »: DCI · Forme · Dosage, or the producer and the type label for a non-médicament
 * (ux/04 §2.1.2) — the producer of its type (refonte WP-101: a complément's laboratoire, its former
 * marque when it has none yet; the marque of the other types).
 */
export function ligneProduit(p) {
  if (!p) return "";
  if (p.type_produit && p.type_produit !== "MEDICAMENT") {
    const marque = p.marque ? String(p.marque).trim() : null;
    const labo = p.laboratoire?.nom ? String(p.laboratoire.nom).trim() : null;
    const producteur = producteurDuType(p.type_produit) === CHAMP_TYPE.LABORATOIRE ? labo || marque : marque;
    // refonte WP-68 — the owner type's label (« Lait ») when the row carries it
    return [producteur || null, p.type_officine?.libelle ?? LOCALE_LABELS[p.type_produit] ?? p.type_produit].filter(Boolean).join(" · ");
  }
  return [dci(p), forme(p), p.dosage].filter(Boolean).join(" · ");
}

/** « N° lot » cell: the number and its barcode (mono, muted). */
export function celluleLot(lot) {
  return {
    numero: lot?.numero_lot ? String(lot.numero_lot) : M.lotNonRenseigne,
    code: lot?.code_barre_lot?.code ?? lot?.code_lot ?? null,
  };
}

/** « Magasin » cell: the principal placement, « + n autres magasins » when split. */
export function celluleMagasin(lot) {
  const p = lot?.placement;
  if (!p) return { nom: M.nonPlace, autres: null };
  const n = Number(p.nb_magasins ?? 1);
  return { nom: p.nom ?? M.nonPlace, autres: n > 1 ? M.autresMagasins(n - 1) : null };
}

/** « Péremption » cell: MM/AAAA + the relative badge, or « Sans péremption » (never 31/12/9999). */
export function cellulePeremption(lot) {
  if (!lot || lot.sans_peremption) return { mois: M.sansPeremption, relatif: null, ton: "neutral" };
  const mois = texteMoisPeremption(lot);
  const j = lot.jours_restants;
  if (j == null) return { mois, relatif: null, ton: "neutral" };
  const relatif = j < 0 ? CARTE_LOT.perimeDepuis(-j) : j === 0 ? CARTE_LOT.aujourdhui : CARTE_LOT.dans(j);
  return { mois, relatif, ton: TON_PEREMPTION[lot.niveau_peremption] ?? "neutral" };
}

/**
 * « Restant » cell: the units and « dont n UG » (D-05). A CAISSIER reads the sellable units as the
 * quantity (server XD-55) — the UG split of the on-hand stock is not shown against it.
 */
export function celluleRestant(lot, { redige = false } = {}) {
  const reste = Number(lot?.quantite_disponible ?? 0);
  const ug = Number(lot?.quantite_ug_disponible ?? 0);
  return { reste, ug: !redige && reste > 0 ? Math.min(ug, reste) : null, texteUg: !redige && reste > 0 ? M.dontUg(Math.min(ug, reste)) : null };
}

/**
 * « État » chip of a lot row: the server's `etat` (ux/04 §2.1.2), the péremption windows labelled
 * from the server thresholds.
 */
export function etatLot(lot, seuils) {
  const e = ETATS_LOT_UI[lot?.etat] ?? ETATS_LOT_UI.VENDABLE;
  if (lot?.etat === "PER_30") return { libelle: `Pér. ≤ ${seuils?.per_30 ?? "…"} j`, ton: e.ton };
  if (lot?.etat === "PER_90") return { libelle: `Pér. ≤ ${seuils?.per_90 ?? "…"} j`, ton: e.ton };
  return { libelle: e.libelle, ton: e.ton };
}

// ─── X XWP-97 — Laboratoire / Prix HT / Stock initial, « n réceptions », « Invisible » ─────────────

/**
 * « Laboratoire » cell of a lot row: the producer of the product's type (refonte WP-101 — a médicament's
 * or complément's laboratoire, else its marque), « — » when none.
 */
export function celluleLaboratoire(lot) {
  const p = lot?.produit;
  const labo = p?.laboratoire?.nom ? String(p.laboratoire.nom).trim() : null;
  const marque = p?.marque ? String(p.marque).trim() : null;
  if (!p?.type_produit || producteurDuType(p.type_produit) === CHAMP_TYPE.LABORATOIRE) return labo || marque || M.nonRenseigne;
  return marque || labo || M.nonRenseigne;
}

/** « Prix HT » of a row: the PU HT, or « min – max » when its receptions were bought at different prices. */
export function cellulePrixHt(lot) {
  if (lot?.prix_achat_ht_min != null && lot?.prix_achat_ht_max != null) return { plage: M.prixPlage(lot.prix_achat_ht_min, lot.prix_achat_ht_max), valeur: null };
  return { plage: null, valeur: lot?.prix_achat_ht ?? null };
}

/** The number of receptions a row sums (XO-25), 1 when the server sent no detail. */
export const nbReceptions = (lot) => Number(lot?.nb_receptions ?? lot?.receptions?.length ?? 1) || 1;

/** « 2 réceptions » under the N° lot, only for a row of several receptions. */
export const texteReceptions = (lot) => (nbReceptions(lot) > 1 ? M.receptions(nbReceptions(lot)) : null);

/** The receptions of a row as the detail lists them, oldest first (the order a sale drains them). */
export function lignesReceptions(lot) {
  return (lot?.receptions ?? []).map((r) => ({
    id: r.id,
    facture: r.facture ? M.receptionFacture(r.facture) || M.receptionSansFacture : M.receptionSansFacture,
    facture_id: r.facture?.id ?? null,
    recue: M.receptionRecue(r.created_at),
    pu_ht: r.prix_achat_ht ?? null,
    quantite: M.receptionQuantite(r.quantite_disponible ?? 0, r.quantite_initiale ?? 0),
    bloque: M.receptionBloque(r.quantite_bloquee),
    invisible: r.invisible_comptoir === true,
  }));
}

/** The « Invisible » badge of a lot row (P / M; a CAISSIER never receives the flag): all, some, none. */
export function badgeVisibilite(lot) {
  if (lot?.invisible_comptoir === true) return { libelle: M.invisible, aide: M.invisibleAide };
  if (lot?.invisible_partiel === true) {
    const n = (lot.receptions ?? []).filter((r) => r.invisible_comptoir).length;
    return { libelle: M.invisiblePartiel(n, nbReceptions(lot)), aide: M.invisibleAide };
  }
  return null;
}

/** Every lot id a page row stands for (its receptions), for the targeted refresh (XA-17). */
export const idsDesLignes = (rows) => new Set((rows ?? []).flatMap((r) => r?.lot_ids ?? (r?.id ? [r.id] : [])));

// ─── Cells of a product row (alert views, ux/04 §2.1.1) ─────────────────────────────────────────

/** « Laboratoire » cell of a product row (the brand when no laboratoire). */
export function laboratoireDuProduit(p) {
  return p?.laboratoire?.nom ?? (p?.marque ? `${String(p.marque).trim()} (marque)` : M.nonRenseigne);
}

/** « État » of a product row: Rupture / Stock bas (server `niveau`, XD-01). */
export function etatProduit(p) {
  return NIVEAUX_ALERTE_UI[p?.niveau] ?? NIVEAUX_ALERTE_UI.BAS;
}

/** The explanation line under the filter chip of a product view (X §3.3). */
export function explicationVue(vue) {
  if (vue === STOCK_VIEWS.ALERTES) return M.alerteExplication;
  if (vue === STOCK_VIEWS.RUPTURES) return M.ruptureExplication;
  if (vue === STOCK_VIEWS.STOCK_BAS) return M.stockBasExplication;
  return null;
}

// ─── Facets (02_DESIGN §3.1, XD-08) ───────────────────────────────────────────────────────────────

/**
 * The facet payload of `GET /stock/facettes` with one `count` per value whatever the row kind (lot
 * views count `lots`, product views `produits`) — the shape `withFacetOptions` reads.
 * @param {object|null} f
 */
export function facettesNormalisees(f) {
  if (!f) return null;
  const out = { ...f };
  for (const def of FILTRES_STOCK) {
    if (!def.facet || !Array.isArray(f[def.facet])) continue;
    out[def.facet] = f[def.facet].map((v) => ({ ...v, count: v.lots ?? v.produits ?? 0 }));
  }
  return out;
}

// ─── Targeted refresh (XA-17, XMD-01-19) ─────────────────────────────────────────────────────────

/**
 * Whether a `stock` topic event concerns the page: the coarse keys (« stock:lots », « stock:alertes »)
 * always, else a `lot:<id>` / `produit:<id>` the page shows. A resync re-reads everything.
 * @param {string[]|null|undefined} cles
 * @param {{ lotIds: Set<string>, produitIds: Set<string>, lignes: 'lots'|'produits' }} page
 */
export function concerneLaPage(cles, { lotIds, produitIds, lignes }) {
  if (!Array.isArray(cles) || cles.length === 0) return true;
  for (const c of cles) {
    if (c === "stock:lots" || c === "stock:facettes") return true;
    if (c === "stock:alertes" && lignes === "produits") return true;
    if (c.startsWith("lot:") && lotIds.has(c.slice(4))) return true;
    if (c.startsWith("produit:") && produitIds.has(c.slice(8))) return true;
  }
  return false;
}

/** Whether a `stock` topic event moves the views strip / topbar counts. */
export function concerneLesCompteurs(cles) {
  if (!Array.isArray(cles) || cles.length === 0) return true;
  return cles.some((c) => c === "stock:alertes" || c === "stock:lots");
}

/** The URL parameters a filter / view change drops (back to page 1, the panel closed). */
export const PARAMS_REINITIALISES = Object.freeze([STOCK_PARAMS.PAGE, STOCK_PARAMS.FOCUS]);
